<template>
  <div v-if="venueCampaign">
    <vx-card no-shadow>
      <div>
        <h2 class="mt-4">Statistics</h2>
      </div>

      <vs-row class="mt-2">
        <vs-row v-if="venueCampaign.hasSentEmail">
          <h4>Email</h4>
          <vs-row>
            <vs-collapse class="w-full">
              <vs-collapse-item>
                <div slot="header">
                  Email guest recipient list ({{
                    venueCampaign.stats.emailNumGuestsSent
                  }})
                </div>
                <vs-list>
                  <vs-list-item
                    v-for="guest in venueCampaign.stats.emailGuestRecipients"
                    :key="guest.guestId"
                    :title="guest.guestDisplayName"
                    :subtitle="guest.guestId"
                  >
                    <vs-chip :color="guest.sentSuccess ? '' : 'warning'">{{
                      guest.sentSuccess ? "Success" : "Send failed"
                    }}</vs-chip>
                  </vs-list-item>
                </vs-list>
              </vs-collapse-item>
            </vs-collapse>
          </vs-row>

          <vs-row class="mt-5 mb-4" vs-justify="center">
            <vs-col
              vs-w="2"
              class="px-4"
              v-for="statsCard in statsCardList"
              :key="statsCard.name"
            >
              <vs-tooltip
                position="bottom"
                delay="140ms"
                :text="statsCard.tooltip"
              >
                <vs-card class="mb-0">
                  <vs-row vs-justify="space-between">
                    <span class="mt-5 ml-3">
                      <feather-icon
                        :icon="statsCard.icon"
                        svgClasses="h-7 w-7 mb-1 stroke-current text-primary"
                      />
                    </span>
                    <span class="text-2xl mt-3 mr-5">
                      <b>{{ statsCard.number }}</b>
                    </span>
                  </vs-row>
                  <vs-row class="mt-6">
                    <h5 class="ml-3">{{ statsCard.name }}</h5>
                  </vs-row>
                </vs-card>
              </vs-tooltip>
            </vs-col>
          </vs-row>

          <div class="w-full">
            <vue-apex-charts
              v-if="emailStats"
              height="500"
              type="line"
              :options="options"
              :series="series"
            ></vue-apex-charts>
          </div>

          <div
            v-if="!emailStats"
            id="div-with-loading"
            style="height: 500px"
            class="vs-con-loading__container"
          ></div>
        </vs-row>
        <vs-row v-if="venueCampaign.hasSentSms">
          <h4>Sms</h4>
          <vs-row>
            <vs-collapse class="w-full">
              <vs-collapse-item>
                <div slot="header">
                  Sms guest recipient list ({{
                    venueCampaign.stats.smsNumGuestsSent
                  }})
                </div>
                <vs-list>
                  <vs-list-item
                    v-for="guest in venueCampaign.stats.smsGuestRecipients"
                    :key="guest.guestId"
                    :title="guest.guestDisplayName"
                    :subtitle="guest.guestId"
                  >
                    <vs-chip :color="guest.sentSuccess ? '' : 'warning'">{{
                      guest.sentSuccess ? "Success" : "Send failed"
                    }}</vs-chip>
                  </vs-list-item>
                </vs-list>
              </vs-collapse-item>
            </vs-collapse>
          </vs-row>
        </vs-row>
        <vs-row v-if="venueCampaign.hasSentNpPush">
          <h4>NightPay Push</h4>
          <vs-row>
            <vs-collapse class="w-full">
              <vs-collapse-item>
                <div slot="header">
                  NightPay Push guest recipient list ({{
                    venueCampaign.stats.npPushNumGuestsSent
                  }})
                </div>
                <vs-list>
                  <vs-list-item
                    v-for="guest in venueCampaign.stats.npPushGuestRecipients"
                    :key="guest.guestId"
                    :title="guest.guestDisplayName"
                    :subtitle="guest.guestId"
                  >
                    <vs-chip :color="guest.sentSuccess ? '' : 'warning'">{{
                      guest.sentSuccess ? "Success" : "Send failed"
                    }}</vs-chip>
                  </vs-list-item>
                </vs-list>
              </vs-collapse-item>
            </vs-collapse>
          </vs-row>
        </vs-row>
      </vs-row>
    </vx-card>

    <vx-card no-shadow> </vx-card>
  </div>
</template>

<script>
import _ from "lodash";
import campaignMixin from "@/views/mixins/campaignMixin";
import { mapGetters } from "vuex";
import VueApexCharts from "vue-apexcharts";

export default {
  props: ["venueCampaign", "isEditable"],
  mixins: [campaignMixin],
  components: {
    VueApexCharts,
  },
  computed: {
    ...mapGetters({
      emailStats: "venueCampaigns/venueCampaignStatistics",
    }),

    statsCardList() {
      return [
        {
          name: "Processed",
          icon: "CpuIcon",
          number: !this.emailStats
            ? 0
            : _.sumBy(this.emailStats, (e) => e.stats[0].metrics.processed),
          tooltip:
            "Total number of emails sent for this campaign in this period",
        },
        {
          name: "Delivered",
          icon: "MailIcon",
          number: !this.emailStats
            ? 0
            : _.sumBy(this.emailStats, (e) => e.stats[0].metrics.delivered),
          tooltip:
            "Total number of emails that were succesfully delivered to the guests in this period",
        },
        {
          name: "Opens",
          icon: "BookOpenIcon",
          number: !this.emailStats
            ? 0
            : `${_.sumBy(
                this.emailStats,
                (e) => e.stats[0].metrics.opens
              )} (${_.sumBy(
                this.emailStats,
                (e) => e.stats[0].metrics.unique_opens
              )})`,
          tooltip:
            "Total number of opened emails in this period. Number in parentheses is the unique count",
        },
        {
          name: "Clicks",
          icon: "ExternalLinkIcon",
          number: !this.emailStats
            ? 0
            : `${_.sumBy(
                this.emailStats,
                (e) => e.stats[0].metrics.clicks
              )} (${_.sumBy(
                this.emailStats,
                (e) => e.stats[0].metrics.unique_clicks
              )})`,
          tooltip:
            "Total number of clicked links in this period. Number in parentheses is the unique count",
        },

        {
          name: "Bounces",
          icon: "CornerUpLeftIcon",
          number: !this.emailStats
            ? 0
            : `${_.sumBy(
                this.emailStats,
                (e) => e.stats[0].metrics.bounces
              )} (${_.sumBy(
                this.emailStats,
                (e) => e.stats[0].metrics.bounce_drops
              )})`,
          tooltip:
            "Total number of bounces (and bounce drops in parentheses) in this period. A bounce is when the email cannot be delivered, typically because the email is invalid",
        },
        {
          name: "Blocks",
          icon: "ShieldOffIcon",
          number: !this.emailStats
            ? 0
            : _.sumBy(this.emailStats, (e) => e.stats[0].metrics.blocks),
          tooltip:
            "Total number of blocked emails in this period. This typically means that the mail has been categorized as spam or the sending mail has been blocked",
        },
      ];
    },
    options() {
      return {
        colors: [
          "#845EC2",
          "#0089BA",
          "#D65DB1",
          "#FF6F91",
          "#FF9671",
          "#FFC75F",
          "#B0A8B9",
          "#4B4453",
        ],
        xaxis: {
          categories: this.emailStats.map((e) => e.date),
        },
      };
    },
    series() {
      return [
        {
          name: "Processed",
          data: this.emailStats.map((e) => e.stats[0].metrics.processed),
        },
        {
          name: "Delivered",
          data: this.emailStats.map((e) => e.stats[0].metrics.delivered),
        },
        {
          name: "Opens",
          data: this.emailStats.map((e) => e.stats[0].metrics.opens),
        },
        {
          name: "Unique opens",
          data: this.emailStats.map((e) => e.stats[0].metrics.unique_opens),
        },
        {
          name: "Clicks",
          data: this.emailStats.map((e) => e.stats[0].metrics.clicks),
        },
        {
          name: "Unique clicks",
          data: this.emailStats.map((e) => e.stats[0].metrics.unique_clicks),
        },
        {
          name: "Bounces",
          data: this.emailStats.map((e) => e.stats[0].metrics.bounces),
        },
        {
          name: "Blocks",
          data: this.emailStats.map((e) => e.stats[0].metrics.blocks),
        },
      ];
    },
  },
  mounted() {
    this.$store.dispatch(
      "venueCampaigns/getVenueCampaignStatistics",
      this.venueCampaign.id
    );
  },
};
</script>


<style lang="scss" scoped>
.smsContentContainer textarea {
  height: 100%;
}
</style>